
import React, { useState, useEffect } from 'react';
import { Wrench, Briefcase, Save, Loader2, CheckCircle, MapPin, Navigation, Mail, Lock } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { checkContentForViolation } from '../services/moderationService';
import { API_BASE_URL } from '@/config';
import { UserRole } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

interface ServiceProviderFormProps {
  onComplete: () => void;
  onBlockUser: () => void;
  role: UserRole;
  walletAddress?: string;
  mode?: 'join' | 'edit';
}

const ServiceProviderForm: React.FC<ServiceProviderFormProps> = ({ 
  onComplete, 
  onBlockUser, 
  walletAddress,
  mode = 'join', 
  role
}) => {
  const { notify } = useToast();
  const { isNewWeb3User } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    serviceName: '',
    contactPerson: '',
    email: '',
    password: '',
    location: '',
    walletAddress: walletAddress || '',
    servicesOffered: '',
    phone: '',
    website: '',
    portfolioUrl: '',
    companyName: '',
    companyRegistrationDetails: '',
  });

  useEffect(() => {
    setFormData(prev => ({ ...prev, walletAddress: walletAddress || '' }));
  }, [walletAddress]);

  const handleAutoDetectLocation = () => {
    if (!navigator.geolocation) {
      notify('Geolocation is not supported by your browser.', 'warn');
      return;
    }
    setIsDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const locationString = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
          setFormData(prev => ({ ...prev, location: locationString }));
          notify('Location detected. You might want to format it to City, Country.', 'info');
        } catch {
          notify('Could not fetch city and country from coordinates.', 'error');
        } finally {
          setIsDetectingLocation(false);
        }
      },
      () => {
        notify('Unable to retrieve your location. Please check your browser permissions.', 'error');
        setIsDetectingLocation(false);
      }
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const parseErrorResponse = async (response: Response) => {
    const responseText = await response.text();
    if (!responseText) return null;
    try {
      return JSON.parse(responseText);
    } catch {
      return { error: responseText };
    }
  };

  const handleJoinSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);
    
    const requiredFields = ['serviceName', 'contactPerson', 'email', 'password', 'location', 'servicesOffered'];

    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    if (missingFields.length > 0) {
        setSubmitError(`Please fill out all required fields: ${missingFields.join(', ')}.`);
        setIsSubmitting(false);
        return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
        setSubmitError('Please enter a valid email address.');
        setIsSubmitting(false);
        return;
    }
    if (formData.password.length < 8) {
        setSubmitError('Password must be at least 8 characters long.');
        setIsSubmitting(false);
        return;
    }

    if (checkContentForViolation(Object.values(formData).join(' '))) {
      onBlockUser();
      setIsSubmitting(false);
      return;
    }
    
    const { password, ...restOfFormData } = formData;
    const finalPayload = {
      name: formData.serviceName,
      role,
      email: formData.email,
      password: formData.password,
      walletAddress: formData.walletAddress || null,
      formData: restOfFormData
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/join-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to submit application.');
      }
      setFormSubmitted(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'An unknown error occurred.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    if (checkContentForViolation(Object.values(formData).join(' '))) {
      onBlockUser();
      setIsSubmitting(false);
      return;
    }
    try {
      const { password, ...restOfFormData } = formData;
      const response = await fetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(restOfFormData),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new Error(errorData?.message || errorData?.error || 'Failed to update profile.');
      }
      notify('Profile updated successfully!', 'success');
      onComplete();
    } catch (error) {
      let message = 'An unknown error occurred.';
      if (error instanceof Error) {
          message = error.message;
      }
      notify(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (formSubmitted) {
    return (
      <div className="text-center py-10">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-white">Application Submitted!</h3>
        <p className="text-kala-300 mt-2">Thank you for your interest. An admin will review your details shortly.</p>
        <button onClick={onComplete} className="mt-6 bg-kala-secondary text-kala-900 font-bold py-2 px-4 rounded-lg">Finish</button>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { 
      e.preventDefault(); 
      if (mode === 'edit') {
        handleEditSubmit(e);
      } else {
        handleJoinSubmit();
      }
    }} className="space-y-6 pt-8">
      <h3 className="text-xl font-bold text-white flex items-center gap-2"><Briefcase className="text-purple-400" /> Business Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input required name="serviceName" onChange={handleChange} value={formData.serviceName} placeholder="Service / Company Name" className="bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <input name="companyName" onChange={handleChange} value={formData.companyName} placeholder="Company Name (If different)" className="bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <textarea name="companyRegistrationDetails" onChange={handleChange} value={formData.companyRegistrationDetails} placeholder="Company Registration Details (Optional)" rows={3} className="md:col-span-2 bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <input required name="contactPerson" onChange={handleChange} value={formData.contactPerson} placeholder="Primary Contact" className="bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />

        <div className="relative">
            <Mail className="absolute left-3 top-3 w-4 h-4 text-kala-500" />
            <input required type="email" name="email" onChange={handleChange} value={formData.email} placeholder="Contact Email" className="w-full bg-kala-900 border border-kala-700 pl-10 pr-4 py-2 rounded-lg text-white outline-none focus:border-kala-secondary" />
        </div>

        <div className="relative">
            <Lock className="absolute left-3 top-3 w-4 h-4 text-kala-500" />
            <input required type="password" name="password" onChange={handleChange} value={formData.password} placeholder="Password (8+ characters)" className="w-full bg-kala-900 border border-kala-700 pl-10 pr-4 py-2 rounded-lg text-white outline-none focus:border-kala-secondary" />
        </div>

        <input name="phone" onChange={handleChange} value={formData.phone} placeholder="Phone (Optional)" className="bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <input name="website" onChange={handleChange} value={formData.website} placeholder="Website URL" className="md:col-span-2 bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <div className="flex items-center gap-2 md:col-span-2">
            <div className="relative w-full">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input required name="location" onChange={handleChange} value={formData.location} className="w-full bg-kala-900 border border-kala-700 rounded-lg pl-10 pr-4 py-2 text-white outline-none focus:border-kala-secondary" placeholder="Location" />
            </div>
            <button 
                type="button"
                onClick={handleAutoDetectLocation}
                disabled={isDetectingLocation}
                className="bg-kala-700 p-2.5 rounded-lg hover:bg-kala-600 transition-colors disabled:opacity-50"
            >
                {isDetectingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
            </button>
        </div>
      </div>
      <div className="border-t border-kala-700/50"></div>
      <h3 className="text-xl font-bold text-white flex items-center gap-2"><Wrench className="text-green-400" /> Service Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <textarea required name="servicesOffered" rows={4} onChange={handleChange} value={formData.servicesOffered} placeholder="List of Services Offered (e.g., Sound Engineering, Lighting, Visual Effects)" className="md:col-span-2 bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <input name="portfolioUrl" onChange={handleChange} value={formData.portfolioUrl} placeholder="Portfolio URL (e.g., Dribbble, Behance)" className="md:col-span-2 bg-kala-900 border border-kala-700 p-2 rounded-lg text-white" />
        <div className="md:col-span-2">
          <label className="block text-xs text-kala-400 mb-1 uppercase font-bold">Wallet Address</label>
          <div className="relative">
            <input
              name="walletAddress"
              onChange={handleChange}
              value={formData.walletAddress}
              className={`w-full bg-kala-900 border rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary ${
                walletAddress && formData.walletAddress === walletAddress
                  ? 'border-green-500/50'
                  : 'border-kala-700'
              }`}
              placeholder="0x... or connect wallet for auto-fill"
            />
            {walletAddress && formData.walletAddress === walletAddress && (
                <CheckCircle className="absolute right-3 top-2.5 w-5 h-5 text-green-500" title="Wallet address automatically filled"/>
            )}
          </div>
           <p className="text-[10px] text-kala-500 mt-1">
              {mode === 'join' && isNewWeb3User ? 'Your connected wallet is pre-filled. You can override if needed.' : 'Optional: Add a wallet for crypto payments.'}
            </p>
        </div>
      </div>
      <button 
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-kala-secondary text-kala-900 font-bold py-4 rounded-xl hover:bg-cyan-400 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/20 disabled:opacity-50"
      >
        {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
        {mode === 'edit' ? 'Save Changes' : 'Submit Service Provider Application'}
      </button>
      {submitError && <p className="text-red-500 mt-4 text-center">{submitError}</p>}
    </form>
  );
};

export default ServiceProviderForm;
